import React from "react";
import { useTranslation } from "react-i18next";
import { Order, OrderGroupDetail } from "@/api/models/api-types";
import { formatPrice } from "../utils/format-price";

interface OrderDetailHeaderSectionProps {
  orderGroupId: string;
  orders: Order[];
  orderGroupDetail?: OrderGroupDetail;
  totalAmount: number;
  currency: string;
}

export const OrderDetailHeaderSection: React.FC<
  OrderDetailHeaderSectionProps
> = ({ orderGroupId, orders, orderGroupDetail, totalAmount, currency }) => {
  const { t, i18n } = useTranslation();

  // Calculate total items count
  const totalItems = orders.reduce((sum, order) => sum + order.items.length, 0);

  // Get order date
  const orderDate = orderGroupDetail?.createdAt
    ? new Date(orderGroupDetail.createdAt)
    : orders[0]?.createdAt
    ? new Date(orders[0].createdAt)
    : new Date();

  // Format date
  const formattedDate = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(orderDate);

  // Merchant/satıcı sayısı
  const merchantCount = orders.length;
  const productCount = totalItems;

  // Status bazlı ürün sayıları (Order.status → items.length)
  const getItemCountByStatuses = (statuses: string[]) =>
    orders
      .filter((o) => statuses.includes(String(o.status).toLowerCase()))
      .reduce((sum, o) => sum + o.items.length, 0);

  const deliveredCount  = getItemCountByStatuses(["delivered"]);
  const refundedCount   = getItemCountByStatuses(["refunded"]);
  const canceledCount   = getItemCountByStatuses(["canceled", "cancelled"]);
  const failedCount     = getItemCountByStatuses(["failed"]);
  const inProgressCount = getItemCountByStatuses(["pending", "paid", "processing", "shipped"]);

  return (
    <div className="border border-border-primary dark:border-gray-700 rounded-lg px-3 md:px-4 py-2 md:py-3">
      {/* Mobile: Grid 2,2,1 layout | Desktop: Flex layout */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:flex md:flex-wrap md:items-end md:gap-x-14 md:gap-y-3">
        {/* Order Date - Row 1, Col 1 */}
        <div className="flex flex-col gap-0.5">
          <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400">
            {t("orders.detail.orderDate")}
          </p>
          <p className="text-xs md:text-sm font-medium text-text-secondary dark:text-gray-200 break-words">
            {formattedDate}
          </p>
        </div>

        {/* Items - Row 1, Col 2 */}
        <div className="flex flex-col gap-0.5">
          <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400">
            {t("orders.detail.items")}
          </p>
          <p className="text-xs md:text-sm font-medium text-text-secondary dark:text-gray-200">
            {merchantCount} {t("orders.detail.delivery")}, {productCount}{" "}
            {t("orders.detail.products")}
          </p>
        </div>

        {/* Order Number - Row 2, Col 1 */}
        <div className="flex flex-col gap-0.5">
          <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400">
            {t("orders.detail.orderNumber")}
          </p>
          <p className="text-xs md:text-sm font-medium text-text-secondary dark:text-gray-200 break-all">
            {orderGroupId}
          </p>
        </div>

        {/* Total - Row 2, Col 2 */}
        <div className="flex flex-col gap-0.5">
          <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400">
            {t("orders.detail.total")}
          </p>
          <p className="text-xs md:text-sm font-medium text-text-secondary dark:text-gray-200">
            {formatPrice(totalAmount, currency)}
          </p>
        </div>

        {/* Order Summary - Row 3, Full Width (Mobile) | Normal (Desktop) */}
        <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1 md:flex-1 md:w-auto md:min-w-[400px]">
          <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400">
            {t("orders.detail.orderSummary")}
          </p>
          <p className="text-xs md:text-sm font-medium leading-4 md:leading-5 flex flex-wrap gap-x-1">
            {/* Delivered */}
            {deliveredCount > 0 && (
              <span className="text-success dark:text-green-400">
                {deliveredCount} {t("orders.detail.productsWereDelivered")}
              </span>
            )}

            {/* In progress */}
            {inProgressCount > 0 && (
              <>
                {deliveredCount > 0 && (
                  <span className="text-text-secondary dark:text-gray-200">,</span>
                )}
                <span className="text-amber-600 dark:text-amber-400">
                  {inProgressCount} {t("orders.detail.productsInProgress")}
                </span>
              </>
            )}

            {/* Refunded */}
            {refundedCount > 0 && (
              <>
                {(deliveredCount > 0 || inProgressCount > 0) && (
                  <span className="text-text-secondary dark:text-gray-200">,</span>
                )}
                <span className="text-orange-600 dark:text-orange-400">
                  {refundedCount} {t("orders.detail.productsWereReturned")}
                </span>
              </>
            )}

            {/* Canceled / Failed */}
            {(canceledCount > 0 || failedCount > 0) && (
              <>
                {(deliveredCount > 0 || inProgressCount > 0 || refundedCount > 0) && (
                  <span className="text-text-secondary dark:text-gray-200">,</span>
                )}
                <span className="text-destructive dark:text-red-400">
                  {canceledCount + failedCount} {t("orders.detail.productsWereCanceled")}
                </span>
              </>
            )}

            {/* Fallback: hiçbir durum yoksa */}
            {deliveredCount === 0 && inProgressCount === 0 && refundedCount === 0 && canceledCount === 0 && failedCount === 0 && (
              <span className="text-text-secondary dark:text-gray-400">
                {productCount} {t("orders.detail.products")}
              </span>
            )}

            <span className="text-text-secondary dark:text-gray-200">.</span>
          </p>
        </div>
      </div>
    </div>
  );
};
